/**
 * RouteErrorBoundary · pantalla amigable cuando una ruta tira error.
 *
 * Conectada en `errorElement` de cada nivel del router. Reemplaza el cartel
 * default de react-router ("Unexpected Application Error!") que asusta y no
 * dice nada útil. Acá hablamos en castellano simple, damos opciones claras
 * (recargar / volver al inicio / contactar) y dejamos los detalles técnicos
 * COLAPSADOS — visibles solo si el user los expande.
 *
 * Regla UI/UX (Denis): que lo entienda un empleado nuevo sin contexto técnico.
 * Nada de stack traces a la vista, nada de "schema", "RLS", "Postgres", etc.
 */
import { AlertTriangle, ChevronDown, Home, RotateCw } from 'lucide-react';
import { useState } from 'react';
import {
  Link,
  isRouteErrorResponse,
  useNavigate,
  useRouteError,
} from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const [showDetails, setShowDetails] = useState(false);

  // Clasificamos el error para mostrar copy correspondiente.
  const meta = classifyError(error);

  return (
    <div className="flex w-full items-center justify-center px-4 py-10">
      <Card className="border-border bg-card flex w-full max-w-[480px] flex-col gap-5 p-6">
        {/* Header con icono + eyebrow mono */}
        <div className="flex items-start gap-3.5">
          <div
            className={cn(
              'flex h-11 w-11 shrink-0 items-center justify-center rounded-lg',
              meta.tone === 'warn'
                ? 'bg-fg-warn/15 text-fg-warn'
                : 'bg-destructive/15 text-destructive',
            )}
          >
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <p className="section-label">{meta.eyebrow}</p>
            <h1 className="text-foreground mt-1 text-heading font-semibold leading-tight tracking-tight">
              {meta.title}
            </h1>
            <p className="text-muted-foreground mt-2 text-body leading-relaxed">
              {meta.message}
            </p>
          </div>
        </div>

        {/* Acciones primarias — lenguaje claro de qué hace cada una */}
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            size="sm"
            className="bg-primary hover:bg-primary/85 h-9 flex-1 text-body font-semibold"
            onClick={() => {
              // Recargar la ruta actual. Más simple y suficiente para 90% de
              // los errores de carga (red, JSON parse, llamadas a Supabase).
              window.location.reload();
            }}
          >
            <RotateCw className="h-3.5 w-3.5" />
            Probar de nuevo
          </Button>
          <Button
            asChild
            size="sm"
            variant="outline"
            className="h-9 flex-1 text-body font-semibold"
            onClick={() => navigate('/dashboard')}
          >
            <Link to="/dashboard">
              <Home className="h-3.5 w-3.5" />
              Ir al inicio
            </Link>
          </Button>
        </div>

        {/* Línea de ayuda */}
        <p className="text-muted-foreground border-border-dim border-t pt-3 text-center text-caption leading-relaxed">
          Si el problema sigue, escribinos a{' '}
          <a
            href="mailto:central@dendev.ar?subject=Reporte%20de%20error%20Core"
            className="text-primary font-medium hover:underline"
          >
            central@dendev.ar
          </a>{' '}
          y contanos qué hiciste antes de que apareciera esta pantalla.
        </p>

        {/* Detalles técnicos — colapsados, solo para desarrolladores */}
        <details
          className="border-border-dim group rounded-md border bg-surface-hi"
          open={showDetails}
          onToggle={(e) => setShowDetails((e.target as HTMLDetailsElement).open)}
        >
          <summary className="text-fg-muted hover:text-foreground flex cursor-pointer select-none items-center justify-between gap-2 px-3 py-2 text-micro">
            <span className="font-medium">Detalles técnicos</span>
            <ChevronDown
              className={cn(
                'h-3.5 w-3.5 transition-transform',
                showDetails && 'rotate-180',
              )}
            />
          </summary>
          <div className="border-border-dim border-t px-3 py-2">
            <pre className="text-fg-muted max-h-40 overflow-auto whitespace-pre-wrap break-words font-mono text-micro leading-relaxed">
              {meta.detail}
            </pre>
          </div>
        </details>
      </Card>
    </div>
  );
}

// ── classifyError ───────────────────────────────────────────────────────────
// Mapea cualquier shape de error a un copy amigable. Sin jerga técnica visible.

interface ErrorMeta {
  eyebrow: string;
  title: string;
  message: string;
  detail: string;
  tone: 'warn' | 'err';
}

function classifyError(error: unknown): ErrorMeta {
  // react-router error de ruta (404, 401, 500 server)
  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return {
        eyebrow: 'no encontrado',
        title: 'Esta página no existe',
        message:
          'El link que abriste ya no apunta a ninguna parte, o quizás te equivocaste de URL. No es nada grave — volvé al inicio y desde ahí buscá lo que necesitabas.',
        detail: `404 · ${error.statusText || 'Not Found'}`,
        tone: 'warn',
      };
    }
    if (error.status === 401 || error.status === 403) {
      return {
        eyebrow: 'sin permiso',
        title: 'No podés ver esta página',
        message:
          'Tu cuenta no tiene permiso para abrir esta sección. Si pensás que deberías poder, pedile a tu admin que te active el acceso.',
        detail: `${error.status} · ${error.statusText || 'Forbidden'}`,
        tone: 'warn',
      };
    }
    return {
      eyebrow: `error ${error.status}`,
      title: 'Algo salió mal',
      message:
        'Pasó algo inesperado al cargar esta página. Probá apretar "Probar de nuevo". Si sigue fallando, escribinos.',
      detail: `${error.status} · ${error.statusText}\n${
        typeof error.data === 'string' ? error.data : JSON.stringify(error.data)
      }`,
      tone: 'err',
    };
  }

  // Error JS estándar
  if (error instanceof Error) {
    // Heurística simple para errores de red.
    const msg = error.message.toLowerCase();
    if (
      msg.includes('failed to fetch') ||
      msg.includes('network') ||
      msg.includes('offline')
    ) {
      return {
        eyebrow: 'sin conexión',
        title: 'No te llega la red',
        message:
          'No se pudo conectar con el servidor. Revisá si tenés internet y probá de nuevo. Si tu wifi anda bien, puede ser un problema momentáneo.',
        detail: `${error.name}: ${error.message}\n\n${error.stack ?? ''}`,
        tone: 'warn',
      };
    }
    return {
      eyebrow: 'error inesperado',
      title: 'Algo salió mal',
      message:
        'Pasó algo que no esperábamos. Probá "Probar de nuevo" — si vuelve a pasar, escribinos contándonos qué estabas haciendo y te ayudamos.',
      detail: `${error.name}: ${error.message}\n\n${error.stack ?? ''}`,
      tone: 'err',
    };
  }

  // Fallback genérico — algún throw no-Error
  return {
    eyebrow: 'error inesperado',
    title: 'Algo salió mal',
    message:
      'Pasó algo raro. Probá recargar; si sigue, escribinos así lo miramos.',
    detail: JSON.stringify(error, null, 2) || String(error),
    tone: 'err',
  };
}
